import { TEXT_STYLES } from 'common/Prose/constants'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { useTextStyle } from 'common/Prose/Toolbar/hooks/useTextStyle'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useContext } from 'react'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import Styles from '../SingleMenuDropdown/SingleMenuDropdown.module.scss'
import { FontFamilyGroup } from '../FontFamilyGroup/FontFamilyGroup'
import { SelectFontSize } from '../FontCharacteristics/SelectFontSize'
import { FontWeight } from '../../components/Format/components/MainStylePane/components/FontWeight/FontWeight'
import { getEntityIdFromCurrentUrl } from 'utils/urlStoreUtils'
import { ArticleService } from 'api/services/ArticleService'
import {
  DefaultArticleStyle,
  TextStyleModal,
} from '../../components/Format/components/MainStylePane/components/TextBlock/components/TextStyleModal/TextStyleModal'
import { handleResponseWithMessage } from 'api/utils/handleResponseWithMessage'
import { SingleMenuDropdownContext } from '../SingleMenuDropdown/SingleMenuDropdownStore'
import { SingleMenuDropdownItem } from '../SingleMenuDropdown/SingleMenuDropdownItem'
import cx from 'classnames'
import { SimpleDropdown } from '../SimpleDropdown/SimpleDropdown'
import { useModals } from 'almanac-ui/Modal'
import { ToolbarResponsiveContext } from '../stores/ToolbarResponsiveStore'
import { IconName, ToolbarIcon } from 'common/Prose/Toolbar/ToolbarIcon'

export const TextStyleDropdown = observer(() => {
  const responsiveStore = useContext(ToolbarResponsiveContext)

  return (
    <SimpleDropdown
      label="Select text style"
      tooltip={{ label: 'Text style' }}
      buttonClassName={cx(Styles.textStylesButton, Styles.dropdownButton, {
        [Styles.useIcon]: responsiveStore?.isSceneA,
      })}
      bodyClassName={Styles.dropdownBody}
    >
      <TextStyleDropdownAnchor />
      <>
        <TextStyleNormal />
        <TextStyleTitle />
        <TextStyleHeading1 />
        <TextStyleHeading2 />
        <TextStyleHeading3 />
        <span className={Styles.lineDivider} />
        <FontFamilyGroup inToolbar />
        <FontWeight inToolbar />
        <SelectFontSize inToolbar />
        <span className={Styles.lineDivider} />
        <TextStyleEdit />
        <ResetTextStylesButton />
      </>
    </SimpleDropdown>
  )
})

const TextStyleDropdownAnchor = observer(() => {
  const responsiveStore = useContext(ToolbarResponsiveContext)
  const almanacEditorStateContext = useContext(AlmanacEditorStateContext)
  const activeStyles = almanacEditorStateContext?.activeStyles

  const currentStyleType = computed(() => {
    const keys = Object.keys(TEXT_STYLES)
    return keys.find((key) => activeStyles?.has(key)) || keys[0]
  }).get()

  if (responsiveStore?.isSceneA) {
    const Icon = TEXT_STYLES[currentStyleType].icon
    return <Icon />
  }
  return <span>{TEXT_STYLES[currentStyleType].textValue}</span>
})

const TextStyleNormal = observer(() => {
  const view = useEditorView()
  const { active, handleClick } = useTextStyle('normal', view)
  const label = TEXT_STYLES['normal'].textValue

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.NormalText} />}
      label={label}
      active={active}
      handleClick={handleClick}
    />
  )
})

const TextStyleTitle = observer(() => {
  const view = useEditorView()
  const { active, handleClick } = useTextStyle('title', view)
  const label = TEXT_STYLES['title'].textValue

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.Title} />}
      label={label}
      active={active}
      handleClick={handleClick}
    />
  )
})

const TextStyleHeading1 = observer(() => {
  const view = useEditorView()
  const { active, handleClick } = useTextStyle('heading1', view)
  const label = TEXT_STYLES['heading1'].textValue

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.H1} />}
      label={label}
      active={active}
      handleClick={handleClick}
    />
  )
})

const TextStyleHeading2 = observer(() => {
  const view = useEditorView()
  const { active, handleClick } = useTextStyle('heading2', view)
  const label = TEXT_STYLES['heading2'].textValue

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.H2} />}
      label={label}
      active={active}
      handleClick={handleClick}
    />
  )
})

const TextStyleHeading3 = observer(() => {
  const view = useEditorView()
  const { active, handleClick } = useTextStyle('heading3', view)
  const label = TEXT_STYLES['heading3'].textValue

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.H3} />}
      label={label}
      active={active}
      handleClick={handleClick}
    />
  )
})

const TextStyleEdit = observer(() => {
  const { openModal } = useModals()
  const dropdownStore = useContext(SingleMenuDropdownContext)

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.Edit} />}
      label="Edit all text styles"
      handleClick={() => {
        dropdownStore?.hardClose()
        openModal(TextStyleModal)
      }}
    />
  )
})

const ResetTextStylesButton = observer(() => {
  const articleId = getEntityIdFromCurrentUrl()
  const dropdownStore = useContext(SingleMenuDropdownContext)

  const resetToDefaultStyles = async () => {
    if (!articleId) return Promise.reject()
    dropdownStore?.hardClose()

    await ArticleService.updateArticle(articleId, { article_style_attributes: DefaultArticleStyle }).then(
      handleResponseWithMessage({ successMessage: 'Doc styles reset to default' }),
    )
  }

  return (
    <SingleMenuDropdownItem
      icon={<ToolbarIcon name={IconName.Reset} />}
      label="Reset to default styles"
      handleClick={resetToDefaultStyles}
    />
  )
})
